import { useOktaAuth } from "@okta/okta-react";
import { useCallback, useMemo } from "react";
import { useLogoutLoading } from "../contexts/logout-loading-context";

export interface AuthState {
	isAuthenticated: boolean;
	isLoading: boolean;
	user: {
		email?: string;
		name?: string;
		sub?: string;
	} | null;
}

export interface AuthActions {
	login: (redirectUrl?: string) => Promise<void>;
	logout: () => Promise<void>;
}

export const useAuth = (): AuthState & AuthActions => {
	const { authState, oktaAuth } = useOktaAuth();
	const { setIsLoggingOut } = useLogoutLoading();

	const user = useMemo(() => {
		if (!authState?.isAuthenticated || !authState.idToken) {
			return null;
		}

		return {
			email: authState.idToken.claims.email as string | undefined,
			name: authState.idToken.claims.name as string | undefined,
			sub: authState.idToken.claims.sub as string | undefined,
		};
	}, [authState]);

	const login = useCallback(
		async (redirectUrl = "/tenants") => {
			await oktaAuth.signInWithRedirect({
				originalUri: redirectUrl,
			});
		},
		[oktaAuth],
	);

	const logout = useCallback(async () => {
		setIsLoggingOut(true);
		await oktaAuth.signOut();
	}, [oktaAuth, setIsLoggingOut]);

	return {
		isAuthenticated: authState?.isAuthenticated ?? false,
		isLoading: authState === null,
		user,
		login,
		logout,
	};
};
